import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ScheduleIcon from '@mui/icons-material/Schedule';

import { useFetchTicketAreas } from '@/shared/actions/erp/tickets-soporte-cliente/gestion-tickets-catalogos.actions';
import { useFetchRoles } from '@/shared/actions/erp/tickets-soporte-cliente/gestion-accesos.actions';
import {
  useFetchRotacionPorArea,
  useToggleRotacion,
  useCrearSlot,
  useActualizarSlot,
  useEliminarSlot,
  type IRotacionSlot,
} from '@/shared/actions/erp/tickets-soporte-cliente/rotacion-roles.actions';
import type { ITicketArea } from '@/shared/interfaces/erp/tickets-soporte-clientes';

import SlotFormDialog from '../components/SlotFormDialog';

const DIAS_LABEL: Record<number, string> = {
  1: 'Lun',
  2: 'Mar',
  3: 'Mié',
  4: 'Jue',
  5: 'Vie',
  6: 'Sáb',
  7: 'Dom',
};

const ZONAS = [
  'America/Bogota',
  'America/Lima',
  'America/Mexico_City',
  'America/Guayaquil',
];

export default function RotacionRolesPage() {
  const { data: areasRes, isLoading: loadingAreas } = useFetchTicketAreas();
  const { data: rolesRes } = useFetchRoles();
  const areas: ITicketArea[] = useMemo(
    () => (areasRes as any)?.data ?? [],
    [areasRes],
  );
  const roles: any[] = (rolesRes as any)?.data ?? [];

  const [areaSeleccionada, setAreaSeleccionada] = useState<ITicketArea | null>(null);

  const { data: rotacionRes, isLoading: loadingRot } = useFetchRotacionPorArea(
    areaSeleccionada?.id_area ?? null,
  );
  const rotacion = (rotacionRes as any)?.data?.rotacion ?? null;
  const slots: IRotacionSlot[] = (rotacionRes as any)?.data?.slots ?? [];

  const toggleMut = useToggleRotacion();
  const crearMut = useCrearSlot();
  const updateMut = useActualizarSlot();
  const eliminarMut = useEliminarSlot();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<IRotacionSlot | null>(null);

  const slotsOrdenados = useMemo(
    () =>
      [...slots].sort((a, b) =>
        a.dia_semana !== b.dia_semana
          ? a.dia_semana - b.dia_semana
          : a.hora_inicio.localeCompare(b.hora_inicio),
      ),
    [slots],
  );

  // Nota: el gating de acceso lo hace el backend. Cualquier mutation sin
  // permiso (rotacion.ver / rotacion.gestionar) devuelve 403 y se muestra
  // en el alert global.

  const handleToggleArea = (area: ITicketArea, activa: boolean) => {
    toggleMut.mutate({
      areaId: area.id_area,
      payload: { activa, zona_horaria: rotacion?.zona_horaria || 'America/Bogota' },
    });
  };

  const handleSubmitSlot = (payload: any) => {
    if (!areaSeleccionada) return;
    if (editing) {
      updateMut.mutate(
        { idSlot: editing.id_slot, areaId: areaSeleccionada.id_area, payload },
        {
          onSuccess: () => {
            setDialogOpen(false);
            setEditing(null);
          },
        },
      );
    } else {
      crearMut.mutate(
        { areaId: areaSeleccionada.id_area, payload },
        {
          onSuccess: () => setDialogOpen(false),
        },
      );
    }
  };

  const handleEliminar = (slot: IRotacionSlot) => {
    if (!areaSeleccionada) return;
    if (!window.confirm(`¿Eliminar el slot ${DIAS_LABEL[slot.dia_semana]} ${slot.hora_inicio}-${slot.hora_fin}?`))
      return;
    eliminarMut.mutate({ idSlot: slot.id_slot, areaId: areaSeleccionada.id_area });
  };

  const handleZonaHorariaChange = (zona: string) => {
    if (!areaSeleccionada) return;
    toggleMut.mutate({
      areaId: areaSeleccionada.id_area,
      payload: {
        activa: !!rotacion?.activa,
        zona_horaria: zona,
      },
    });
  };

  const nombreRol = (rolId: string) =>
    roles.find((r: any) => r.id_rol === rolId)?.nombre ?? rolId.slice(0, 8);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <ScheduleIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          Rotación de roles por área
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Calendario semanal recurrente. Si una área tiene rotación activa, en cada momento
        del día los usuarios asumen el rol que define su slot. Fuera de los slots (festivos,
        horas no cubiertas) cae al rol estático para no perder accesos.
      </Typography>

      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Áreas ({areas.length})
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Área</TableCell>
                <TableCell align="center">Rotación activa</TableCell>
                <TableCell>Configurar</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingAreas && (
                <TableRow>
                  <TableCell colSpan={3} align="center">Cargando áreas...</TableCell>
                </TableRow>
              )}
              {!loadingAreas && areas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <em>No hay áreas configuradas</em>
                  </TableCell>
                </TableRow>
              )}
              {areas.map((a) => {
                const esta = areaSeleccionada?.id_area === a.id_area;
                const activa = esta && !!rotacion?.activa;
                return (
                  <TableRow key={a.id_area} hover selected={esta}>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {a.color && (
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: a.color,
                              flexShrink: 0,
                            }}
                          />
                        )}
                        <Typography variant="body2" fontWeight={500}>{a.nombre}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={esta ? activa : false}
                        disabled={toggleMut.isPending}
                        onChange={(e) => {
                          if (!esta) setAreaSeleccionada(a);
                          handleToggleArea(a, e.target.checked);
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant={esta ? 'contained' : 'outlined'}
                        onClick={() => setAreaSeleccionada(a)}
                      >
                        {esta ? 'Seleccionada' : 'Configurar'}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {areaSeleccionada && (
        <Paper variant="outlined">
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Slots de {areaSeleccionada.nombre}
                </Typography>
                {rotacion?.activa ? (
                  <Chip label="ROTACIÓN ACTIVA" size="small" color="success" />
                ) : (
                  <Chip label="INACTIVA — usa rol estático" size="small" />
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  Zona horaria:
                </Typography>
                <select
                  value={rotacion?.zona_horaria || 'America/Bogota'}
                  onChange={(e) => handleZonaHorariaChange(e.target.value)}
                  disabled={toggleMut.isPending}
                  style={{ padding: '4px 8px', borderRadius: 4 }}
                >
                  {ZONAS.map((z) => (
                    <option key={z} value={z}>{z}</option>
                  ))}
                </select>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  variant="contained"
                  onClick={() => {
                    setEditing(null);
                    setDialogOpen(true);
                  }}
                >
                  Nuevo slot
                </Button>
              </Stack>
            </Stack>
          </Box>

          {!rotacion?.activa && (
            <Alert severity="info" sx={{ m: 2 }}>
              La rotación está <strong>desactivada</strong> para esta área. Los usuarios usan
              su rol estático (api2_usuario_rol). Activá el switch arriba cuando los slots
              estén configurados.
            </Alert>
          )}

          {loadingRot ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>Cargando slots...</Box>
          ) : slotsOrdenados.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Aún no hay slots. Click en "Nuevo slot" para agregar uno.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Día</TableCell>
                    <TableCell>Hora</TableCell>
                    <TableCell>Usuario</TableCell>
                    <TableCell>Rol aplicado</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {slotsOrdenados.map((s) => (
                    <TableRow key={s.id_slot} hover>
                      <TableCell>
                        <Chip
                          label={DIAS_LABEL[s.dia_semana] ?? s.dia_semana}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {s.hora_inicio?.slice(0, 5)} – {s.hora_fin?.slice(0, 5)}
                      </TableCell>
                      <TableCell>
                        {s.usuario_nombre || `Usuario ${s.usuario_id}`}
                      </TableCell>
                      <TableCell>
                        <Chip label={nombreRol(s.rol_id)} size="small" color="primary" />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditing(s);
                              setDialogOpen(true);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleEliminar(s)}
                            disabled={eliminarMut.isPending}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Divider />
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Validación: no se permiten slots solapados para el mismo usuario en el mismo día.
              Si el momento actual no cae en ningún slot, se aplica el rol estático.
            </Typography>
          </Box>
        </Paper>
      )}

      <SlotFormDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSubmitSlot}
        loading={crearMut.isPending || updateMut.isPending}
        initial={editing}
      />
    </Box>
  );
}
